import React, { useMemo } from 'react';
import { Button, Divider, Grid } from '@mui/material';
import { AppDayName } from './appDayName';
import { AppCalendarActivityDay } from './appCalendarActivityDay';
import { Activity } from '../../../models/activity';
import {
  getIntervalDayMonthAccording,
  getWeeksNumberInMonth,
} from '../../../services/dateService';
import { isSameDay, isSameMonth } from 'date-fns';
import {
  getTotalHours,
  getTargetHoursToWork,
} from '../../../services/activityService';
import { ColorPalette } from '../../../styles/constants';
import { useGetDaysOffQuery } from '../../../store/daysOff/daysOffService';
import {
   formatDateOnly,
} from '../../../services/dateService';

const style = {
  container: {
    marginBottom: '10px',
    textAlign: 'center',
  },
  divider: {
    margin: '10px 0 10px 0',
  },

  grid: {
    width: '100%',
  },
  button: {
    backgroundColor: ColorPalette.DARK_GRAY,
    color: ColorPalette.WHITE,
    marginBottom: '.5rem',
    height: '75px',
    minWidth: 0,
    width: '100%',
    p: 1.5,

    '&:hover': {
      color: ColorPalette.CHINESE_BLACK,
      backgroundColor: ColorPalette.MIDDLE_BLUE,
    },
  },
} as const;

interface Props {
  referenceDate: Date;
  location?: string;
  activities: Activity[];
  onSelectDay(date: string): void;
  onSelectWeek(weekNumber: number): void;
  showOnlyloggedTime?: boolean;
  employeeWorkHours?: number;
}

const DAY_OF_WEEKS = ['', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export function AppCalendarActivity({
  referenceDate,
  activities,
  location,
  onSelectDay,
  onSelectWeek,
  showOnlyloggedTime = false,
  employeeWorkHours,
}: Props): JSX.Element {
  const weeksNumber = useMemo(
    () => getWeeksNumberInMonth(referenceDate),
    [referenceDate]
  );

  const { data: targetHolidays } = useGetDaysOffQuery({
    filterDate: new Date(referenceDate).getFullYear(),
    country: location || '',
  });

  const intervalOfDaysActivities = useMemo(() => {
    const intervalOfDays = getIntervalDayMonthAccording(referenceDate);

    return intervalOfDays.map((day) => {
      day = formatDateOnly(day);
      const isSameMonthDates = isSameMonth(Date.parse(formatDateOnly(day)), referenceDate);

      const daysOffByDay = targetHolidays?.find((freeDay) =>
        
        isSameDay(Date.parse(formatDateOnly(freeDay.dayOffDate)), Date.parse(formatDateOnly(day)))
      );
      const activitiesByDay = activities.filter((activity) =>{
        let parse1=Date.parse(formatDateOnly(activity.date));
        let parse2=Date.parse(formatDateOnly(day));
        
        return isSameDay(parse1, parse2);
      }
      );

      const totalHoursWorkedDay = activitiesByDay.length
        ? getTotalHours(activitiesByDay)
        : 0;

      const targetHoursToWork = (!showOnlyloggedTime && employeeWorkHours) 
        ? getTargetHoursToWork(
          new Date(formatDateOnly(day)),
          targetHolidays,
          location || '',
          employeeWorkHours) 
        : 0;

      return {
        isSameMonthDates,
        totalHoursWorkedDay,
        day,
        daysOffByDay,
        targetHoursToWork,
      };
    });
  }, [referenceDate, activities, targetHolidays, location, employeeWorkHours, showOnlyloggedTime]);

  return (
    <Grid container spacing={1} sx={style.container}>
      {DAY_OF_WEEKS.map((day) => (
        <AppDayName day={day} key={day} />
      ))}
      <Grid item xs={12}>
        <Divider sx={style.divider} />
      </Grid>
      <Grid container sx={{ flexWrap: 'nowrap' }} spacing={1}>
        <Grid xs={0.8} item sx={{ mr: 1 }}>
          {weeksNumber.map((weekNumber) => (
            <Grid item xs={12} key={weekNumber} sx={style.grid}>
              <Button
                sx={style.button}
                onClick={() => onSelectWeek(weekNumber)}
              >
                {weekNumber}
              </Button>
            </Grid>
          ))}
        </Grid>
        <Grid xs={11.2} item container spacing={1}>
          {intervalOfDaysActivities.map(
            ({
              day,
              isSameMonthDates,
              totalHoursWorkedDay,
              daysOffByDay,
              targetHoursToWork,
            }) => (
              <AppCalendarActivityDay
                key={day}
                day={day}
                isSameMonth={isSameMonthDates}
                totalHoursWorked={totalHoursWorkedDay}
                onSelectDay={onSelectDay}
                dayOff={daysOffByDay}
                targetHoursToWork={targetHoursToWork}
                showOnlyloggedTime={showOnlyloggedTime}
              />
            )
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
